import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Switch, 
  Button, 
  Row, 
  Col, 
  Tabs, 
  message, 
  Typography, 
  Space, 
  Tooltip,
  Collapse,
  Alert
} from 'antd';
import { 
  SettingOutlined, 
  TeamOutlined, 
  SafetyCertificateOutlined, 
  EnvironmentOutlined,
  InfoCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../../../services/api';

const { Option } = Select;
const { Text } = Typography;
const { Panel } = Collapse;

interface ARKConfigProps {
  serverId: string;
  gameType: string;
  serverInfo: any;
  onTabChange?: (tabKey: string) => void;
}

const ARKConfig: React.FC<ARKConfigProps> = ({ serverId, gameType, serverInfo, onTabChange }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState<any>(null);
  const [currentLaunchParams, setCurrentLaunchParams] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userServerCount, setUserServerCount] = useState<number>(0);

  const mapOptions = [
    { value: 'TheIsland', label: '孤岛', description: '原始地图' },
    { value: 'TheCenter', label: '中心岛', description: '多样化地形' },
    { value: 'ScorchedEarth', label: '焦土', description: '沙漠环境' },
    { value: 'Ragnarok', label: '诸神黄昏', description: '北欧神话' },
    { value: 'Aberration', label: '畸变', description: '地下世界' },
    { value: 'Extinction', label: '灭绝', description: '未来世界' },
    { value: 'Valguero', label: '瓦尔盖罗', description: '多样化生物群系' },
    { value: 'Genesis', label: '创世纪', description: '太空站' },
    { value: 'CrystalIsles', label: '水晶岛', description: '水晶世界' },
    { value: 'Genesis2', label: '创世纪2', description: '太空站续作' },
    { value: 'LostIsland', label: '失落岛', description: '热带岛屿' },
    { value: 'Fjordur', label: '峡湾', description: '北欧神话世界' },
    { value: 'Ragnarok', label: '仙境', description: '梦幻仙境世界' }
  ];

  const getGameModeDescription = (mode: string) => {
    const descriptions = {
      pve: '玩家对抗环境，不能攻击其他玩家',
      pvp: '玩家对抗玩家，可以攻击其他玩家',
      pvpve: '混合模式，既有PvE也有PvP元素'
    };
    return descriptions[mode as keyof typeof descriptions] || '';
  };

  useEffect(() => {
    loadConfig();
    loadCurrentUser();
  }, [serverId]);

  const loadCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      
      // 获取ARK游戏类型ID
      const gameTypesResponse = await api.get('/games/types');
      const arkGameType = gameTypesResponse.data.find((gameType: any) => gameType.name === 'ark');
      
      if (arkGameType) {
        // 获取用户的ARK服务器数量
        const serversResponse = await api.get('/users/servers');
        const arkServers = serversResponse.data.filter((server: any) => server.game_type_id === arkGameType.id);
        setUserServerCount(arkServers.length);
      } else {
        setUserServerCount(0);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  };

  const isAdmin = currentUser?.role === 'admin';

  // 监听表单值变化，实时更新启动参数
  useEffect(() => {
    const updateLaunchParams = () => {
      const values = form.getFieldsValue();
      if (values) {
        setCurrentLaunchParams(generateLaunchParams(values));
      }
    };

    // 初始更新
    updateLaunchParams();
  }, [configData, form]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/servers/${serverId}/config`);
      const configData = response.data;
      
      // 获取服务器信息，包括admin_password字段
      const serverResponse = await api.get(`/servers/${serverId}`);
      const serverData = serverResponse.data;
      
      // 处理新的配置结构
      let formValues = {};
      
      if (configData.basic) {
        // 新结构：从basic、server中提取值
        formValues = {
          // 基本设置
          map: configData.basic.map || 'TheIsland',
          gameMode: configData.basic.gameMode || 'pvp',
          gamePort: configData.basic.gamePort || serverInfo?.port,
          queryPort: configData.basic.queryPort || serverInfo?.query_port || (serverInfo?.port ? serverInfo.port + 1 : undefined),
          rconPort: configData.basic.rconPort || (serverInfo?.port ? serverInfo.port + 2 : undefined),
          maxPlayers: configData.basic.maxPlayers || serverInfo?.max_players || 20,
          adminPassword: serverData.admin_password || configData.basic.adminPassword,
          publishPlatform: configData.basic.publishPlatform || 'Steam',
          enableMultiServer: configData.basic.enableMultiServer,
          autoManagedMods: configData.basic.autoManagedMods,
          
          // 服务器设置
          networkOptimization: configData.server?.networkOptimization || '直连线路',
          allowCheats: configData.server?.allowCheats !== undefined ? configData.server.allowCheats : true,
          useBattlEye: configData.server?.useBattlEye !== undefined ? configData.server.useBattlEye : true,
          allowThirdPerson: configData.server?.allowThirdPerson !== undefined ? configData.server.allowThirdPerson : true,
          allowFlying: configData.server?.allowFlying !== undefined ? configData.server.allowFlying : true,
          serverCrosshair: configData.server?.serverCrosshair !== undefined ? configData.server.serverCrosshair : true,
          allowCaveBuilding: configData.server?.allowCaveBuilding !== undefined ? configData.server.allowCaveBuilding : false,
          allowTribeWar: configData.server?.allowTribeWar !== undefined ? configData.server.allowTribeWar : false,
          allowTribeAlliances: configData.server?.allowTribeAlliances !== undefined ? configData.server.allowTribeAlliances : true
        };
      } else {
        // 旧结构：直接使用配置数据
        formValues = {
          ...configData,
          map: configData.map || 'TheIsland',
          gameMode: configData.gameMode || 'pvp',
          gamePort: configData.gamePort || serverInfo?.port,
          queryPort: configData.queryPort || serverInfo?.query_port || (serverInfo?.port ? serverInfo.port + 1 : undefined),
          rconPort: configData.rconPort || (serverInfo?.port ? serverInfo.port + 2 : undefined),
          maxPlayers: configData.maxPlayers || serverInfo?.max_players || 20,
          // 设置默认值
          allowCheats: configData.allowCheats !== undefined ? configData.allowCheats : true,
          useBattlEye: configData.useBattlEye !== undefined ? configData.useBattlEye : true,
          allowThirdPerson: configData.allowThirdPerson !== undefined ? configData.allowThirdPerson : true,
          allowFlying: configData.allowFlying !== undefined ? configData.allowFlying : true,
          serverCrosshair: configData.serverCrosshair !== undefined ? configData.serverCrosshair : true,
          allowCaveBuilding: configData.allowCaveBuilding !== undefined ? configData.allowCaveBuilding : false,
          allowTribeWar: configData.allowTribeWar !== undefined ? configData.allowTribeWar : false,
          allowTribeAlliances: configData.allowTribeAlliances !== undefined ? configData.allowTribeAlliances : true
        };
      }
      
      setConfigData(formValues);
      form.setFieldsValue(formValues);
    } catch (error) {
      console.error('加载配置失败:', error);
      message.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (values: any) => {
    try {
      setLoading(true);
      
      // 获取表单的所有值，包括未修改的字段
      const allValues = form.getFieldsValue();
      console.log('保存时的所有表单值:', allValues);
      
      // 生成启动参数
      const customLaunchParams = generateLaunchParams(allValues);
      
      // 按照新的配置结构组织数据
      const configToSave = {
        basic: {
          map: allValues.map,
          gameMode: allValues.gameMode,
          gamePort: allValues.gamePort,
          queryPort: allValues.queryPort,
          rconPort: allValues.rconPort,
          maxPlayers: allValues.maxPlayers,
          adminPassword: allValues.adminPassword,
          publishPlatform: allValues.publishPlatform || 'Steam',
          enableMultiServer: allValues.enableMultiServer,
          autoManagedMods: allValues.autoManagedMods
        },
        server: {
          networkOptimization: allValues.networkOptimization,
          allowCheats: allValues.allowCheats,
          useBattlEye: allValues.useBattlEye,
          allowThirdPerson: allValues.allowThirdPerson,
          allowFlying: allValues.allowFlying,
          serverCrosshair: allValues.serverCrosshair,
          allowCaveBuilding: allValues.allowCaveBuilding,
          allowTribeWar: allValues.allowTribeWar,
          allowTribeAlliances: allValues.allowTribeAlliances
        },
        customLaunchParams
      };
      
      console.log('保存的配置:', configToSave);
      
      // 保存到数据库的config字段（用于配置管理-服务器配置管理-ARK服务器配置）
      await api.put(`/servers/${serverId}/config`, { config: configToSave });
      
      // 如果管理员密码有变化，更新数据库中的admin_password字段
      if (allValues.adminPassword !== undefined) {
        try {
          await api.put(`/servers/${serverId}`, { 
            admin_password: allValues.adminPassword 
          });
          console.log('管理员密码已更新到数据库:', allValues.adminPassword);
        } catch (error) {
          console.error('更新管理员密码到数据库失败:', error);
          // 不抛出错误，因为主要配置已保存成功
        }
      }
      
      // 更新启动脚本文件
      try {
        await api.post(`/servers/${serverId}/update-startup-script`, {
          launchCommand: customLaunchParams
        });
        message.success('配置保存成功，启动脚本已更新');
      } catch (scriptError) {
        console.error('更新启动脚本失败:', scriptError);
        message.warning('配置保存成功，但启动脚本更新失败');
      }
      
      // 重新加载配置以确保数据同步
      await loadConfig();
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 生成ARK启动参数
  const generateLaunchParams = (values: any) => {
    const gamePort = values.gamePort || serverInfo?.port || 7777;
    const queryPort = values.queryPort || serverInfo?.query_port || (gamePort + 1);
    const rconPort = values.rconPort || (gamePort + 2);
    
    // 构建基础参数 - 使用不同的连接方式
    let queryParams = [
      `${values.map || 'TheIsland'}?listen`
    ];
    
    // 添加网络优化参数
    if (values.networkOptimization === '直连线路2') {
      queryParams.push(`?MultiHome=10.10.11.10`);
    } else if (values.networkOptimization === '阿里云优化') {
      queryParams.push(`?MultiHome=10.10.12.10`);
    }
    
    // 添加端口参数
    queryParams.push(`?Port=${gamePort}`, `?QueryPort=${queryPort}`, `?RCONPort=${rconPort}`, `?MaxPlayers=${values.maxPlayers || 20}`);
    
    // 添加管理员密码（如果设置了）
    if (values.adminPassword) {
      queryParams.push(`?ServerAdminPassword=${values.adminPassword}`);
    }
    
    // 添加游戏模式相关参数
    if (values.gameMode === 'pve') {
      queryParams.push('?bAllowFlyerCarryPvE=true', '?bPvEAllowStructureDamage=true');
    }
    
    // 添加服务器设置参数（默认都添加到命令行，用布尔值控制）
    queryParams.push(`?bAllowCheats=${values.allowCheats !== false ? 'true' : 'false'}`);
    queryParams.push(`?bAllowFlyerCarryPvE=${values.allowFlying !== false ? 'true' : 'false'}`);
    queryParams.push(`?bAllowThirdPersonPlayer=${values.allowThirdPerson !== false ? 'true' : 'false'}`);
    queryParams.push(`?bServerCrosshair=${values.serverCrosshair !== false ? 'true' : 'false'}`);
    queryParams.push(`?bAllowCaveBuildingPvE=${values.allowCaveBuilding === true ? 'true' : 'false'}`);
    queryParams.push(`?bAllowTribeWar=${values.allowTribeWar === true ? 'true' : 'false'}`);
    queryParams.push(`?bAllowTribeAlliances=${values.allowTribeAlliances !== false ? 'true' : 'false'}`);
    
    // 构建命令行参数（-开头的参数）
    let commandParams = ['-server', '-log'];
    
    // 添加发布平台参数（在-server前面）
    const publishPlatform = values.publishPlatform || 'Steam';
    const serverIndex = commandParams.indexOf('-server');
    if (serverIndex !== -1) {
      if (publishPlatform === 'Steam和Epic') {
        commandParams.splice(serverIndex, 0, '-crossplay');
      } else if (publishPlatform === 'Epic') {
        commandParams.splice(serverIndex, 0, '-epiconly');
      }
      // Steam 不需要添加参数
    }
    
    // 添加反作弊参数
    if (values.useBattlEye) {
      commandParams.push('-UseBattlEye');
    }
    
    // 添加多通服参数
    if (values.enableMultiServer) {
      const username = currentUser?.username || 'admin';
      // 在-server之前插入多通服参数（在发布平台参数之后）
      const newServerIndex = commandParams.indexOf('-server');
      if (newServerIndex !== -1) {
        commandParams.splice(newServerIndex, 0, `-clusterid=${username}`, `-ClusterDirOverride="D:\\GameServers\\ARK\\Clusters\\${username}"`, '-NoTransferFromFiltering');
      }
    }
    
    // 添加自动MOD管理参数
    if (values.autoManagedMods) {
      // 在-server后面添加-automanagedmods参数
      const serverIndex = commandParams.indexOf('-server');
      if (serverIndex !== -1) {
        commandParams.splice(serverIndex + 1, 0, '-automanagedmods');
      }
    }
    
    // 连接参数：?参数用空字符串连接，-参数用空格连接
    const queryString = queryParams.join('');
    const commandString = commandParams.join(' ');
    
    return `${queryString} ${commandString}`;
  };

  const tabItems = [
    {
      key: 'basic',
      label: <span><SettingOutlined />基本设置</span>,
      children: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label="发布平台"
                name="publishPlatform"
                tooltip="选择服务器的发布平台，Steam为默认平台，Steam和Epic支持跨平台，Epic仅支持Epic平台"
                initialValue="Steam"
              >
                <Select>
                  <Select.Option value="Steam">Steam</Select.Option>
                  <Select.Option value="Steam和Epic">Steam和Epic</Select.Option>
                  <Select.Option value="Epic">Epic</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="开启多通服"
                name="enableMultiServer"
                valuePropName="checked"
                tooltip="启用多服务器传送功能，允许玩家在不同服务器间传送"
              >
                <Switch 
                  disabled={userServerCount < 2}
                />
              </Form.Item>
              
              {/* 多通服条件提示 */}
              <Alert
                message={userServerCount < 2 ? "多通服功能未满足条件" : "恭喜您满足开启条件"}
                description={
                  <div>
                    {userServerCount < 2 ? (
                      <>
                        <p>开启多通服的条件为必须同时拥有2个及以上的服务器数量才能开启。</p>
                        <p>当前拥有ARK服务器数量：{userServerCount}个</p>
                      </>
                    ) : (
                      <>
                        <p>您已拥有{userServerCount}个ARK服务器，满足开启多通服的条件。</p>
                        <p>现在可以启用多通服功能，允许玩家在不同服务器间传送。</p>
                      </>
                    )}
                  </div>
                }
                type={userServerCount < 2 ? "warning" : "success"}
                showIcon
                className="ark-config-alert-margin"
              />
            </Col>
            <Col span={12}>
              <Form.Item
                label="自动安装/更新 MOD"
                name="autoManagedMods"
                valuePropName="checked"
                tooltip="启用后，系统会在启动时自动下载和更新MOD到最新版本，建议添加MOD后首次启动时启用，减少MOD无需启用此功能，后续可保持关闭此功能提升服务器启动速度。"
              >
                <Switch 
                  disabled={serverInfo?.mod_support_enabled !== 1}
                />
              </Form.Item>
              
              {/* MOD功能未开通提示 */}
              {serverInfo?.mod_support_enabled !== 1 && (
                <Alert
                  message="MOD功能未开通"
                  description={
                    <div>
                      <p>启用此功能需先为此服务器开通MOD功能</p>
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={() => {
                          // 跳转到MOD管理页面
                          if (onTabChange) {
                            onTabChange('mods');
                          } else {
                            window.location.hash = '#mods';
                          }
                        }}
                        style={{ marginTop: '8px' }}
                      >
                        前往MOD管理页面开通
                      </Button>
                    </div>
                  }
                  type="warning"
                  showIcon
                  className="ark-config-alert-margin"
                />
              )}
              
              {/* 自动MOD管理提示 */}
              {serverInfo?.mod_support_enabled === 1 && form.getFieldValue('autoManagedMods') && (
                <Alert
                  message="自动MOD管理"
                  description={
                    <div>
                      <p>先到MOD管理页面进行添加MOD，添加好了之后打开这个按钮，启动游戏时系统会自动下载最新的MOD。（备用功能，需要更新MOD时再启用，此功能影响服务器启动速度），Mod管理页面已经集成添加到安装MOD的全套流程</p>
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={() => {
                          // 跳转到MOD管理页面
                          if (onTabChange) {
                            onTabChange('mods');
                          } else {
                            window.location.hash = '#mods';
                          }
                        }}
                        style={{ marginTop: '8px' }}
                      >
                        前往MOD管理页面
                      </Button>
                    </div>
                  }
                  type="info"
                  showIcon
                  className="ark-config-alert-margin"
                />
              )}
            </Col>
          </Row>

          {/* 多通服提示信息 */}
          {userServerCount >= 2 && form.getFieldValue('enableMultiServer') && (
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Alert
                  message="多通服设置"
                  description="所有服务器必须安装完全相同列表和版本的MOD。如果孤岛有S+和Awesome Spyglass，焦土也必须有完全一样的S+和Awesome Spyglass。MOD ID列表和加载顺序都必须一致，否则会导致传送失败、物品丢失或服务器崩溃。"
                  type="warning"
                  showIcon
                  className="ark-config-alert-margin"
                />
              </Col>
            </Row>
          )}

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="最大玩家数"
                name="maxPlayers"
                rules={[{ required: true, message: '请输入最大玩家数' }]}
              >
                <InputNumber 
                  min={1} 
                  max={1000} 
                  className="ark-config-full-width"
                  placeholder="最大玩家数"
                  readOnly={!isAdmin}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="管理员密码"
                name="adminPassword"
                tooltip="留空表示不设置管理员密码"
              >
                <Input.Password placeholder="管理员密码（可选）" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item
                label="游戏端口"
                name="gamePort"
                rules={[{ required: true, message: '请输入游戏端口' }]}
                tooltip="游戏连接端口，通常为7777"
              >
                <InputNumber 
                  min={1024} 
                  max={65535} 
                  className="ark-config-full-width"
                  placeholder="游戏端口"
                  readOnly={!isAdmin}
                  onChange={(value) => {
                    // 自动计算查询端口和控制台端口
                    if (value && !form.getFieldValue('queryPort')) {
                      form.setFieldValue('queryPort', value + 1);
                    }
                    if (value && !form.getFieldValue('rconPort')) {
                      form.setFieldValue('rconPort', value + 2);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="查询端口"
                name="queryPort"
                rules={[{ required: true, message: '请输入查询端口' }]}
                tooltip="服务器查询端口，通常为游戏端口+1"
              >
                <InputNumber 
                  min={1024} 
                  max={65535} 
                  className="ark-config-full-width"
                  placeholder="查询端口"
                  readOnly={!isAdmin}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="控制台端口"
                name="rconPort"
                rules={[{ required: true, message: '请输入控制台端口' }]}
                tooltip="RCON控制台端口，通常为游戏端口+2"
              >
                <InputNumber 
                  min={1024} 
                  max={65535} 
                  className="ark-config-full-width"
                  placeholder="控制台端口"
                  readOnly={!isAdmin}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label="游戏模式"
                name="gameMode"
                rules={[{ required: true, message: '请选择游戏模式' }]}
                initialValue="pvp"
              >
                <Select placeholder="选择游戏模式">
                  <Option value="pve">
                    <div className="py-3">
                      <div className="font-medium">PvE (玩家对环境)</div>
                      <Text type="secondary" className="ark-config-description-text">
                        {getGameModeDescription('pve')}
                      </Text>
                    </div>
                  </Option>
                  <Option value="pvp">
                    <div className="py-3">
                      <div className="font-medium">PvP (玩家对玩家)</div>
                      <Text type="secondary" className="ark-config-description-text">
                        {getGameModeDescription('pvp')}
                      </Text>
                    </div>
                  </Option>
                  <Option value="pvpve">
                    <div className="py-3">
                      <div className="font-medium">PvPvE (混合模式)</div>
                      <Text type="secondary" className="ark-config-description-text">
                        {getGameModeDescription('pvpve')}
                      </Text>
                    </div>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="地图"
                name="map"
                rules={[{ required: true, message: '请选择地图' }]}
                initialValue="TheIsland"
              >
                <Select placeholder="选择地图" showSearch>
                  {mapOptions.map(map => (
                    <Option key={map.value} value={map.value}>
                      <div className="py-3">
                        <div className="font-medium">{map.label}</div>
                        <Text type="secondary" className="ark-config-description-text">
                          {map.description}
                        </Text>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )
    },
    {
      key: 'server',
      label: <span><SafetyCertificateOutlined />服务器设置</span>,
      children: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>网络优化</span>
                    <Tooltip title="如果用直连线路搜不到服务器，请选择阿里云/腾讯云/京东云优化">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="networkOptimization"
                initialValue="直连线路"
              >
                <Select placeholder="选择网络优化模式" allowClear>
                  <Option value="直连线路">直连线路</Option>
                  <Option value="直连线路2">直连线路2</Option>
                  <Option value="阿里云优化">阿里云优化</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>官方作弊指令</span>
                    <Tooltip title="决定玩家（尤其是管理员）是否能在游戏内使用官方提供的作弊命令">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="allowCheats"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>反作弊</span>
                    <Tooltip title="是否启用第三方的反作弊软件来检测和阻止未经授权的黑客程序、外挂和内存修改器。它防御的是'恶意'的作弊。">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="useBattlEye"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>允许飞行</span>
                    <Tooltip title="允许玩家在服务器中飞行">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="allowFlying"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>允许第三人称</span>
                    <Tooltip title="允许玩家使用第三人称视角">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="allowThirdPerson"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>服务器准星</span>
                    <Tooltip title="显示服务器准星">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="serverCrosshair"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>允许洞穴建筑</span>
                    <Tooltip title="允许在洞穴中建造建筑">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="allowCaveBuilding"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>允许部落战争</span>
                    <Tooltip title="允许部落之间进行战争">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="allowTribeWar"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span>允许部落联盟</span>
                    <Tooltip title="允许部落之间建立联盟">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="allowTribeAlliances"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </>
      )
    },
    {
      key: 'launch-command',
      label: <span><EnvironmentOutlined />启动命令</span>,
      children: (
        <>
          <Form.Item
            label={
              <Space>
                <span>当前启动命令</span>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    const values = form.getFieldsValue();
                    setCurrentLaunchParams(generateLaunchParams(values));
                  }}
                >
                  刷新
                </Button>
              </Space>
            }
            tooltip="根据上述设置自动生成的启动命令"
          >
            <Input.TextArea 
              rows={8}
              value={currentLaunchParams}
              placeholder="启动命令将根据设置自动生成..."
              readOnly
              className="ark-config-launch-command"
            />
          </Form.Item>
          
          {/* 参数解析显示 */}
          <Form.Item label="参数解析" tooltip="启动命令中关键参数的说明">
            <div className="ark-config-params-display">
              {(() => {
                const values = form.getFieldsValue();
                const params = [
                  { key: '地图', value: values.map || 'TheIsland', param: 'map' },
                  { key: '游戏端口', value: values.gamePort || serverInfo?.port || 7777, param: 'Port' },
                  { key: '查询端口', value: values.queryPort || (values.gamePort ? values.gamePort + 1 : (serverInfo?.port ? serverInfo.port + 1 : 7778)), param: 'QueryPort' },
                  { key: '控制台端口', value: values.rconPort || (values.gamePort ? values.gamePort + 2 : (serverInfo?.port ? serverInfo.port + 2 : 7779)), param: 'RCONPort' },
                  { key: '最大玩家', value: values.maxPlayers || 20, param: 'MaxPlayers' },
                  { key: '管理员密码', value: values.adminPassword ? '已设置' : '未设置', param: 'ServerAdminPassword' },
                  { key: '网络优化', value: values.networkOptimization || '直连线路', param: (values.networkOptimization === '阿里云优化' || values.networkOptimization === '直连线路2') ? 'MultiHome' : '' },
                  { key: '官方作弊指令', value: (values.allowCheats !== false) ? '启用' : '禁用', param: 'bAllowCheats' },
                  { key: '反作弊', value: values.useBattlEye ? '启用' : '禁用', param: 'UseBattlEye' },
                  { key: '允许飞行', value: (values.allowFlying !== false) ? '启用' : '禁用', param: 'bAllowFlyerCarryPvE' },
                  { key: '允许第三人称', value: (values.allowThirdPerson !== false) ? '启用' : '禁用', param: 'bAllowThirdPersonPlayer' },
                  { key: '服务器准星', value: (values.serverCrosshair !== false) ? '启用' : '禁用', param: 'bServerCrosshair' },
                  { key: '允许洞穴建筑', value: values.allowCaveBuilding === true ? '启用' : '禁用', param: 'bAllowCaveBuildingPvE' },
                  { key: '允许部落战争', value: values.allowTribeWar === true ? '启用' : '禁用', param: 'bAllowTribeWar' },
                  { key: '允许部落联盟', value: (values.allowTribeAlliances !== false) ? '启用' : '禁用', param: 'bAllowTribeAlliances' },
                  { key: '发布平台', value: values.publishPlatform || 'Steam', param: values.publishPlatform === 'Steam和Epic' ? '-crossplay' : values.publishPlatform === 'Epic' ? '-epiconly' : '' },
                  { key: '多通服', value: values.enableMultiServer ? '启用' : '禁用', param: 'clusterid' },
                  { key: '自动MOD管理', value: values.autoManagedMods ? '启用' : '禁用', param: 'automanagedmods' }
                ];
                
                return params.map(p => (
                  <div key={p.param || p.key} className="ark-config-param-item">
                    <span className="ark-config-param-key">{p.key}:</span> {p.value}
                    {p.param && <span className="ark-config-param-name">(?{p.param})</span>}
                  </div>
                ));
              })()}
            </div>
          </Form.Item>
          
          <div className="ark-config-tip">
            <Text type="secondary">
              <InfoCircleOutlined /> 启动命令会根据上述设置自动生成。修改设置后点击"刷新"按钮可实时查看更新后的启动命令。
            </Text>
          </div>
        </>
      )
    }
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={saveConfig}
        onValuesChange={(changedValues, allValues) => {
          // 表单值变化时更新启动参数
          console.log('表单值变化:', changedValues, allValues);
          setCurrentLaunchParams(generateLaunchParams(allValues));
        }}
        initialValues={configData || {}}
      >
        <Card
          title={
            <Space>
              <SettingOutlined />
              <span>ARK服务器配置</span>
            </Space>
          }
          extra={
            <Button 
              type="primary" 
              onClick={() => form.submit()}
              loading={loading}
            >
              保存配置
            </Button>
          }
        >
          <Tabs 
            defaultActiveKey="basic" 
            size="large"
            items={tabItems}
          />
        </Card>
      </Form>
    </div>
  );
};

export default ARKConfig;